import { useState, useEffect, useMemo, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { formatRelativeTime, formatDateTime } from '../lib/utils';
import { ShieldCheck, RefreshCw, AlertTriangle } from 'lucide-react';

interface AuditLog {
  id: number;
  userId: number | null;
  actorUsername: string | null;
  actorDisplayName: string | null;
  actorRole: 'admin' | 'user' | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  changes: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: string | null;
  createdAt: number;
}

interface AuditLogsResponse {
  data: AuditLog[];
  meta: { total: number; limit: number; offset: number };
}

function prettyJson(raw: string | null): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function summarizeJson(raw: string | null): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return String(parsed);
    }
    const entries = Object.entries(parsed as Record<string, unknown>);
    const summary = entries.slice(0, 3).map(([key, value]) => {
      if (value == null) return `${key}: null`;
      if (typeof value === 'string') {
        const clipped = value.length > 34 ? `${value.slice(0, 34)}...` : value;
        return `${key}: ${clipped}`;
      }
      if (typeof value === 'number' || typeof value === 'boolean')
        return `${key}: ${value}`;
      if (Array.isArray(value)) return `${key}: [${value.length}]`;
      return `${key}: {...}`;
    });
    if (entries.length > 3) summary.push(`+${entries.length - 3} more`);
    return summary.join(' · ');
  } catch {
    return raw.length > 96 ? `${raw.slice(0, 96)}...` : raw;
  }
}

function actorLabel(log: AuditLog): string {
  if (log.actorDisplayName && log.actorUsername) {
    return `${log.actorDisplayName} (@${log.actorUsername})`;
  }
  if (log.actorUsername) return `@${log.actorUsername}`;
  if (log.userId != null) return `#${log.userId}`;
  return 'anonymous';
}

function targetLabel(log: AuditLog): string {
  if (log.entityType && log.entityId)
    return `${log.entityType}#${log.entityId}`;
  if (log.entityType) return log.entityType;
  if (log.entityId) return `#${log.entityId}`;
  return '-';
}

/** Derive a color class from the action suffix */
function actionColor(action: string): string {
  const suffix = action.split('.').pop() ?? '';
  if (suffix === 'login' || suffix === 'logout') return 'text-neon-cyan';
  if (suffix === 'create') return 'text-neon-green';
  if (suffix === 'update') return 'text-neon-yellow';
  if (
    suffix === 'delete' ||
    suffix === 'archive' ||
    suffix === 'deactivate' ||
    suffix === 'reject'
  )
    return 'text-neon-red';
  if (suffix === 'approve' || suffix === 'mark_paid')
    return 'text-neon-magenta';
  return 'text-text-primary';
}

/** Format action string for display: "campaign.create" → "CAMPAIGN · CREATE" */
function formatAction(action: string): { prefix: string; suffix: string } {
  const parts = action.split('.');
  const suffix = parts.pop() ?? action;
  const prefix = parts.join('.').toUpperCase();
  return { prefix, suffix: suffix.toUpperCase() };
}

function ExpandedDetail({ log }: { log: AuditLog }) {
  const color = actionColor(log.action);
  return (
    <tr>
      <td
        colSpan={6}
        className="bg-bg-primary border-b border-border-subtle p-0"
      >
        <div className="px-4 py-3 space-y-3 animate-fade-in">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs font-mono">
            <div>
              <span className="text-text-secondary">Actor: </span>
              <span className={color}>{actorLabel(log)}</span>
              {log.actorRole && (
                <span className="text-text-tertiary"> · {log.actorRole}</span>
              )}
            </div>
            <div>
              <span className="text-text-secondary">Target: </span>
              <span className="text-neon-cyan">{targetLabel(log)}</span>
            </div>
            <div>
              <span className="text-text-secondary">Exact Time: </span>
              <span className="text-text-primary">
                {formatDateTime(log.createdAt)}
              </span>
            </div>
            <div>
              <span className="text-text-secondary">IP Address: </span>
              <span className={color}>{log.ipAddress ?? '-'}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Changes */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-text-secondary font-display mb-1">
                Changes
              </div>
              <pre className="terminal whitespace-pre-wrap break-all overflow-auto max-h-96 text-[11px]">
                {prettyJson(log.changes) || '// no change data'}
              </pre>
            </div>

            {/* Metadata */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-text-secondary font-display mb-1">
                Metadata
              </div>
              <pre className="terminal whitespace-pre-wrap break-all overflow-auto max-h-96 text-[11px]">
                {prettyJson(log.metadata) || '// no metadata'}
              </pre>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 text-xs font-mono">
            <div>
              <span className="text-text-secondary">Audit ID: </span>
              <span className="text-neon-cyan">#{log.id}</span>
            </div>
            <div>
              <span className="text-text-secondary">User ID: </span>
              <span className="text-neon-cyan">{log.userId ?? '-'}</span>
            </div>
            <div>
              <span className="text-text-secondary">Full Action: </span>
              <span className={color}>{log.action}</span>
            </div>
          </div>

          {log.userAgent && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-text-secondary font-display mb-1">
                User Agent
              </div>
              <span className="font-mono text-[10px] text-text-tertiary break-all">
                {log.userAgent}
              </span>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

export function AuditLogPage() {
  const [actionFilter, setActionFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [allLogs, setAllLogs] = useState<AuditLog[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Debounced query params — apply on search
  const [appliedAction, setAppliedAction] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');

  const query = useQuery({
    queryKey: ['audit-logs', appliedAction, appliedUserId, offset],
    queryFn: () =>
      apiClient.getEnvelope<AuditLog[]>('/api/v1/audit', {
        ...(appliedAction ? { action: appliedAction } : {}),
        ...(appliedUserId ? { user_id: Number(appliedUserId) } : {}),
        limit: 100,
        offset,
      }),
    placeholderData: (prev) => prev, // keep showing previous data during refetch — no empty flash
  });

  // Accumulate later pages into allLogs only — first page renders directly
  // from query.data.data so there's no empty-state flicker when component
  // mounts (or on filter change). Avoiding the useState-fed-by-useEffect
  // anti-pattern that caused the "kadang muncul" bug.
  useEffect(() => {
    if (!query.data?.data) return;
    if (offset > 0) {
      setAllLogs((prev) => {
        const existingIds = new Set(prev.map((l) => l.id));
        const newItems = query.data!.data.filter((l) => !existingIds.has(l.id));
        return [...prev, ...newItems];
      });
    }
  }, [query.data, offset]);

  // Reset on filter change
  useEffect(() => {
    setOffset(0);
    setAllLogs([]);
    setExpandedId(null);
  }, [appliedAction, appliedUserId]);

  // Display: when on page 0, use query.data directly (instant). When paginated
  // beyond, merge accumulator with current page so user sees all loaded data.
  const displayedLogs: AuditLog[] =
    offset === 0
      ? (query.data?.data ?? [])
      : [
          ...allLogs,
          ...(query.data?.data ?? []).filter(
            (l) => !allLogs.some((p) => p.id === l.id),
          ),
        ];

  const total = query.data?.meta?.total ?? 0;
  const hasMore = displayedLogs.length < total;

  // ── Failed-login spike detector ────────────────────────────────────────
  // Scans displayedLogs for clusters of `user.login_failed` and surfaces
  // anything that looks like an active brute-force attempt. Two heuristics:
  //   1. >= 20 failed attempts against ONE target (entityId = username|userId)
  //      in the last 1 hour. Indicates someone is grinding a specific account.
  //   2. >= 30 failed attempts from ONE IP in the last 1 hour. Indicates
  //      one host running through a wordlist regardless of target.
  // Either signal renders a warning banner at the top of the page so admin
  // sees it without scrolling. Detection is client-side because we already
  // have the data — avoids a separate /api/v1/security/threats endpoint.
  const spikeAlerts = useMemo(() => {
    const oneHourAgo = Date.now() - 60 * 60_000;
    const recentFailures = displayedLogs.filter(
      (l) => l.action === 'user.login_failed' && l.createdAt > oneHourAgo,
    );

    // Bucket by target (entityId = username or userId)
    const byTarget = new Map<string, AuditLog[]>();
    // Bucket by source IP
    const byIp = new Map<string, AuditLog[]>();
    for (const log of recentFailures) {
      const target = log.entityId ?? 'unknown';
      const arr = byTarget.get(target) ?? [];
      arr.push(log);
      byTarget.set(target, arr);
      if (log.ipAddress) {
        const ipArr = byIp.get(log.ipAddress) ?? [];
        ipArr.push(log);
        byIp.set(log.ipAddress, ipArr);
      }
    }

    const targetSpikes = Array.from(byTarget.entries())
      .filter(([, logs]) => logs.length >= 20)
      .map(([target, logs]) => ({
        type: 'target' as const,
        key: target,
        count: logs.length,
        latestAt: Math.max(...logs.map((l) => l.createdAt)),
      }))
      .sort((a, b) => b.count - a.count);

    const ipSpikes = Array.from(byIp.entries())
      .filter(([, logs]) => logs.length >= 30)
      .map(([ip, logs]) => ({
        type: 'ip' as const,
        key: ip,
        count: logs.length,
        latestAt: Math.max(...logs.map((l) => l.createdAt)),
      }))
      .sort((a, b) => b.count - a.count);

    return {
      targetSpikes,
      ipSpikes,
      totalRecentFailures: recentFailures.length,
    };
  }, [displayedLogs]);

  const hasSpike =
    spikeAlerts.targetSpikes.length > 0 || spikeAlerts.ipSpikes.length > 0;

  const applyFilters = () => {
    setAppliedAction(actionFilter.trim());
    setAppliedUserId(userIdFilter.trim());
  };

  const toggleRow = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest flex items-center gap-2">
            <ShieldCheck size={20} className="text-neon-magenta" />
            AUDIT LOG
          </h1>
          <p className="text-sm text-text-secondary font-mono mt-0.5">
            // every admin action recorded
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          icon={<RefreshCw size={12} />}
          onClick={() => {
            setOffset(0);
            // D6: Don't clear allLogs — placeholderData keeps previous entries visible
            // during the in-flight refetch, preventing an empty-state flash.
            query.refetch();
          }}
          loading={query.isFetching}
        >
          Refresh
        </Button>
      </div>

      {/* Failed-login spike banner — only renders when threshold tripped */}
      {hasSpike && (
        <div className="cyber-panel p-4 border-neon-red/50 bg-neon-red/5">
          <div className="flex items-start gap-3">
            <AlertTriangle
              size={18}
              className="text-neon-red shrink-0 mt-0.5 animate-pulse"
            />
            <div className="flex-1 space-y-2">
              <h3 className="font-display text-sm uppercase tracking-wider text-neon-red">
                Brute-Force Activity Detected
              </h3>
              <p className="text-[11px] font-mono text-text-secondary leading-relaxed">
                {spikeAlerts.totalRecentFailures} failed login
                {spikeAlerts.totalRecentFailures === 1 ? '' : 's'} in the last
                hour. Rate limit is enforced (per-IP + per-username) but worth
                investigating.
              </p>
              {/* Per-target spikes — accounts under attack */}
              {spikeAlerts.targetSpikes.length > 0 && (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-text-tertiary font-display">
                    Targets being attacked:
                  </div>
                  {spikeAlerts.targetSpikes.map((s) => (
                    <div key={`t-${s.key}`} className="text-[11px] font-mono">
                      <span className="text-neon-yellow">{s.count}×</span>
                      <span className="text-text-secondary"> failed for </span>
                      <button
                        onClick={() => {
                          setActionFilter('user.login_failed');
                          setAppliedAction('user.login_failed');
                        }}
                        className="text-neon-cyan hover:underline"
                      >
                        {s.key}
                      </button>
                      <span className="text-text-tertiary">
                        {' '}
                        · last {formatRelativeTime(s.latestAt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {/* Per-IP spikes — wordlist runners */}
              {spikeAlerts.ipSpikes.length > 0 && (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-text-tertiary font-display">
                    Source IPs hammering:
                  </div>
                  {spikeAlerts.ipSpikes.map((s) => (
                    <div key={`i-${s.key}`} className="text-[11px] font-mono">
                      <span className="text-neon-yellow">{s.count}×</span>
                      <span className="text-text-secondary">
                        {' '}
                        attempts from{' '}
                      </span>
                      <span className="text-neon-cyan">{s.key}</span>
                      <span className="text-text-tertiary">
                        {' '}
                        · last {formatRelativeTime(s.latestAt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[10px] font-mono text-text-tertiary pt-1 border-t border-border-subtle/40">
                Defense action: consider adding the source IP to a Cloudflare
                WAF block rule, or rotating the targeted account's password.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="cyber-panel p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <Input
              label="Filter by action"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="e.g. campaign.create"
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            />
          </div>
          <div className="w-40">
            <Input
              label="Filter by user ID"
              value={userIdFilter}
              onChange={(e) =>
                setUserIdFilter(e.target.value.replace(/\D/g, ''))
              }
              placeholder="e.g. 42"
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            />
          </div>
          <Button variant="neon" size="sm" onClick={applyFilters}>
            Apply
          </Button>
          {(appliedAction || appliedUserId) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setActionFilter('');
                setUserIdFilter('');
                setAppliedAction('');
                setAppliedUserId('');
              }}
            >
              Clear
            </Button>
          )}
          <span className="ml-auto self-end text-xs font-mono text-text-tertiary pb-1">
            {total > 0 ? `${displayedLogs.length} / ${total} entries` : ''}
          </span>
        </div>
      </div>

      {/* Table */}
      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Time</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>Detail</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {displayedLogs.map((log) => {
                const { prefix, suffix } = formatAction(log.action);
                const color = actionColor(log.action);
                const detail =
                  summarizeJson(log.changes) || summarizeJson(log.metadata);

                return (
                  <Fragment key={log.id}>
                    <tr
                      onClick={() => toggleRow(log.id)}
                      className={`cursor-pointer transition-colors ${
                        expandedId === log.id
                          ? 'bg-bg-secondary'
                          : 'hover:bg-bg-secondary/50'
                      }`}
                    >
                      <td
                        className="text-xs text-text-tertiary font-mono whitespace-nowrap"
                        title={formatDateTime(log.createdAt)}
                      >
                        {formatRelativeTime(log.createdAt)}
                      </td>
                      <td className="font-mono text-xs">
                        <div className="text-neon-cyan">{actorLabel(log)}</div>
                        {log.actorRole && (
                          <div className="text-[10px] text-text-tertiary uppercase tracking-wider">
                            {log.actorRole}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`font-mono text-xs ${color}`}>
                          {prefix && (
                            <span className="text-text-secondary">
                              {prefix}.
                            </span>
                          )}
                          {suffix}
                        </span>
                      </td>
                      <td className="font-mono text-xs text-text-secondary">
                        {targetLabel(log)}
                      </td>
                      <td className="font-mono text-[11px] text-text-tertiary max-w-xs">
                        <span className="line-clamp-2">{detail || '-'}</span>
                      </td>
                      <td className="font-mono text-xs text-text-tertiary">
                        {log.ipAddress ?? '-'}
                      </td>
                    </tr>
                    {expandedId === log.id && <ExpandedDetail log={log} />}
                  </Fragment>
                );
              })}

              {displayedLogs.length === 0 && !query.isLoading && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-16 text-text-tertiary"
                  >
                    <ShieldCheck
                      className="mx-auto mb-3 opacity-30"
                      size={36}
                    />
                    <p className="font-mono text-xs">no audit entries found</p>
                    {(appliedAction || appliedUserId) && (
                      <p className="font-mono text-[10px] mt-1 text-text-tertiary opacity-60">
                        try clearing your filters
                      </p>
                    )}
                  </td>
                </tr>
              )}

              {query.isLoading && displayedLogs.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-12 text-text-tertiary font-mono text-xs"
                  >
                    loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOffset((o) => o + 100)}
            loading={query.isFetching}
          >
            Load more ({total - displayedLogs.length} remaining)
          </Button>
        </div>
      )}
    </div>
  );
}
